import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Clock, Navigation } from "lucide-react";

const KAFR_NABL = [35.6146, 36.5637];

const driverIcon = () => L.divIcon({
  className: "",
  html: '<div style="width:20px;height:20px;background:#103C30;border:3px solid white;border-radius:50%;box-shadow:0 0 0 6px rgba(16,60,48,0.22);"></div>',
  iconSize: [20, 20], iconAnchor: [10, 10],
});
const destIcon = () => L.divIcon({
  className: "",
  html: '<div style="width:26px;height:26px;transform:translateY(-6px);"><svg viewBox="0 0 24 24" width="26" height="26" fill="#7B284D" stroke="white" stroke-width="1.5"><path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="white" stroke="none"/></svg></div>',
  iconSize: [26, 26], iconAnchor: [13, 20],
});

function haversineKm(a, b) {
  const R = 6371;
  const dLat = (b[0] - a[0]) * Math.PI / 180;
  const dLng = (b[1] - a[1]) * Math.PI / 180;
  const la1 = a[0] * Math.PI / 180, la2 = b[0] * Math.PI / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(h));
}

export default function MapView({ driver, destination, height = 220, testid = "map-view" }) {
  const el = useRef(null);
  const map = useRef(null);
  const dMarker = useRef(null);
  const destM = useRef(null);
  const line = useRef(null);
  const [eta, setEta] = useState(null);

  useEffect(() => {
    if (!el.current || map.current) return;
    const start = driver ? [driver.lat, driver.lng] : (destination ? [destination.lat, destination.lng] : KAFR_NABL);
    map.current = L.map(el.current, { zoomControl: true, attributionControl: false }).setView(start, 14);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map.current);
    return () => { if (map.current) { map.current.remove(); map.current = null; dMarker.current = destM.current = line.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!map.current) return;
    const d = driver ? [driver.lat, driver.lng] : null;
    const dest = destination ? [destination.lat, destination.lng] : null;

    if (d) {
      if (!dMarker.current) dMarker.current = L.marker(d, { icon: driverIcon() }).addTo(map.current);
      else dMarker.current.setLatLng(d);
    }
    if (dest) {
      if (!destM.current) destM.current = L.marker(dest, { icon: destIcon() }).addTo(map.current);
      else destM.current.setLatLng(dest);
    }
    if (!d || !dest) {
      if (d) map.current.setView(d, map.current.getZoom(), { animate: true });
      return;
    }

    let cancelled = false;
    const drawStraight = () => {
      const km = haversineKm(d, dest) * 1.3;
      return { coords: [d, dest], km, mins: Math.max(1, Math.round(km / 25 * 60)), dashed: true };
    };
    (async () => {
      let result = drawStraight();
      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${d[1]},${d[0]};${dest[1]},${dest[0]}?overview=full&geometries=geojson`;
        const res = await fetch(url);
        const j = await res.json();
        if (j.routes && j.routes[0]) {
          result = {
            coords: j.routes[0].geometry.coordinates.map((c) => [c[1], c[0]]),
            km: j.routes[0].distance / 1000,
            mins: Math.max(1, Math.round(j.routes[0].duration / 60)),
            dashed: false,
          };
        }
      } catch (e) { /* fallback to straight line */ }
      if (cancelled || !map.current) return;
      const style = { color: "#103C30", weight: 4, opacity: 0.78, dashArray: result.dashed ? "8 8" : null };
      if (!line.current) line.current = L.polyline(result.coords, style).addTo(map.current);
      else { line.current.setLatLngs(result.coords); line.current.setStyle(style); }
      setEta({ km: result.km.toFixed(1), mins: result.mins });
      map.current.fitBounds(line.current.getBounds(), { padding: [38, 38], maxZoom: 16 });
    })();
    return () => { cancelled = true; };
  }, [driver, destination]);

  return (
    <div className="relative" data-testid={testid}>
      <div ref={el} style={{ height, width: "100%" }} className="rounded-2xl overflow-hidden border border-border relative z-0" />
      {eta && (
        <div className="absolute top-2 right-2 z-[500] glass px-3 py-1.5 rounded-full text-xs font-bold shadow-md flex items-center gap-2" data-testid={`${testid}-eta`}>
          <span className="flex items-center gap-1 text-primary"><Clock className="w-3.5 h-3.5" /> ~{eta.mins} د</span>
          <span className="flex items-center gap-1 text-muted-foreground"><Navigation className="w-3.5 h-3.5" /> {eta.km} كم</span>
        </div>
      )}
    </div>
  );
}
